import {
  Button,
  DateInput,
  Icon,
  Select,
  TextInput,
  addToast,
} from '@octano/global-ui';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import dayjs from 'dayjs';

import Tooltip from '../../../../components/tooltip/Tooltip';
import { useActionKitImp } from '../../../../components/ozmoit/common';
import { useValidations } from '../../../../hooks/useValidations';
import { AdmissionProcess } from '../types';
import {
  updateAdmissionProcessAction,
  createAdmissionProcessAction,
} from './managementActions';
import useFirstInstallmentRule from '../hooks/useFirstInstallmentRule';
import { formatFirstInstallmentRule, getFirstInstallmentRule } from '../utils';

const PREFIX = 'details';

interface Props {
  process?: AdmissionProcess;
  refresh: () => Promise<void>;
  periods?: { id: number; name: string }[];
  processTypes?: {
    id: number;
    name: string;
    templateId?: number | null;
    templates?: { id: number; name: string }[];
  }[];
  defaultPeriodId?: number | null;
  onCancel?: () => void;
  // Forma cruda que entrega PaydayService.getPaydayOptions() (entriesToOptions):
  // { id, name }, no { label, value }. Se remapea abajo, igual que
  // processTypeOptions/templateOptions con sus propias listas crudas.
  paydays?: { id: string; name: string }[];
}

export default function AdmissionProcessDetailsForm({
  process,
  refresh,
  periods = [],
  processTypes = [],
  defaultPeriodId,
  onCancel,
  paydays = [],
}: Props) {
  const { t } = useTranslation('academic-offer/admission-processes');
  const history = useHistory();
  const { msgValidations } = useValidations();
  const isEdit = !!process;

  const processTypeOptions = useMemo(
    () =>
      processTypes.map((pt) => ({
        value: pt.id,
        label: pt.name,
      })),
    [processTypes],
  );

  const paydayOptions = useMemo(
    () =>
      paydays.map((p) => ({
        value: +p.id,
        label: p.name,
      })),
    [paydays],
  );

  const noneTemplateOption = useMemo(
    () => ({
      value: null as number | null,
      label: t('management.form.templateNone'),
    }),
    [t],
  );

  const methods = useForm<any>({
    defaultValues: {
      name: process?.name ?? '',
      tuitionProcessTypeId: process?.tuitionProcessTypeId
        ? {
            value: process.tuitionProcessTypeId,
            label: process.tuitionProcessTypeName,
          }
        : null,
      startedAt:
        process?.startedAt && dayjs(process.startedAt).isValid()
          ? dayjs.utc(process.startedAt).format('YYYY-MM-DD')
          : '',
      endedAt:
        process?.endedAt && dayjs(process.endedAt).isValid()
          ? dayjs.utc(process.endedAt).format('YYYY-MM-DD')
          : '',
      isMultiTuition:
        process?.isMultiTuition !== undefined
          ? {
              value: process.isMultiTuition,
              label: process.isMultiTuition ? t('common.yes') : t('common.no'),
            }
          : null,
      isEnabled:
        process?.isEnabled !== undefined
          ? {
              value: process.isEnabled,
              label: process.isEnabled ? t('common.yes') : t('common.no'),
            }
          : null,
      periodName: process?.periodName ?? '',
      periodId: defaultPeriodId
        ? {
            value: defaultPeriodId,
            label: periods.find((p) => p.id === defaultPeriodId)?.name,
          }
        : null,
      quotas: process?.totalAllocatedQuota ?? '',
      academicOfferQuota: process?.academicOfferQuota ?? '',
      templateId: noneTemplateOption,
      [`${PREFIX}_firstInstallmentRule`]: null,
      maxInstallments: process?.maxInstallments
        ? { value: process.maxInstallments, label: String(process.maxInstallments) }
        : null,
      payday:
        process?.payday !== null && process?.payday !== undefined
          ? { value: process.payday, label: '' }
          : null,
    },
  });

  const firstInstallmentRule = methods.watch(`${PREFIX}_firstInstallmentRule`);

  const tuitionProcessTypeIdValue = methods.watch('tuitionProcessTypeId');
  const selectedProcessType = useMemo(
    () => processTypes.find((pt) => pt.id === tuitionProcessTypeIdValue?.value),
    [processTypes, tuitionProcessTypeIdValue],
  );

  const templateOptions = useMemo(() => {
    const items = (selectedProcessType?.templates ?? []).map((tpl) => ({
      value: tpl.id as number | null,
      label: tpl.name,
    }));
    return [noneTemplateOption, ...items];
  }, [selectedProcessType, noneTemplateOption]);

  useEffect(() => {
    if (!selectedProcessType) {
      return;
    }
    const defaultTemplateId = selectedProcessType.templateId;
    const match = templateOptions.find(
      (opt) => opt.value === defaultTemplateId,
    );
    methods.setValue('templateId', match ?? noneTemplateOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProcessType?.id]);

  const { options, formFields } = useFirstInstallmentRule({
    key: firstInstallmentRule?.value as string,
    control: methods.control,
    fieldPrefix: PREFIX,
  });

  useEffect(() => {
    if (process?.firstInstallmentRule) {
      const {
        key,
        selectDate,
        selectMonth,
        selectYear,
        selectDay,
        validityRange,
      } = getFirstInstallmentRule(process.firstInstallmentRule);

      const selectedOption = options.find((option) => option.value === key);

      methods.setValue(`${PREFIX}_firstInstallmentRule`, {
        label: selectedOption?.label ?? '',
        value: selectedOption?.value ?? '',
      });

      if (selectDate) {
        methods.setValue(`${PREFIX}_selectDate`, selectDate);
      }
      if (selectMonth) {
        methods.setValue(`${PREFIX}_selectMonth`, selectMonth);
      }
      if (selectYear) {
        methods.setValue(`${PREFIX}_selectYear`, selectYear);
      }
      if (selectDay) {
        methods.setValue(`${PREFIX}_selectDay`, selectDay);
      }
      if (validityRange) {
        methods.setValue(`${PREFIX}_validityRange`, validityRange.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process?.firstInstallmentRule]);

  useEffect(() => {
    if (process?.payday === null || process?.payday === undefined) {
      return;
    }
    const match = paydayOptions.find((p) => p.value === process.payday);
    if (!match) {
      // Las opciones pueden no haber llegado todavía (o venir vacías si el
      // frontend se despliega antes que la API). Limpiar el campo aquí haría
      // que el siguiente submit escribiera `payday: null` y borrara en silencio
      // el override configurado, así que se deja el valor por defecto tal cual.
      return;
    }
    methods.setValue('payday', match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process?.payday, paydayOptions]);

  const { action: updateProcess, loading: updating } = useActionKitImp(
    updateAdmissionProcessAction,
    {
      onSuccess: async () => {
        addToast({
          icon: 'success',
          text: t('management.toasts.success'),
          color: 'success',
        });
        await refresh();
      },
      onError: {
        unknown: () => {
          addToast({
            icon: 'error',
            text: t('management.toasts.error'),
            color: 'danger',
          });
        },
      },
    },
  );

  const { action: createProcess, loading: creating } = useActionKitImp(
    createAdmissionProcessAction,
    {
      onSuccess: async (data: any) => {
        addToast({
          icon: 'success',
          text: t('management.toasts.success'),
          color: 'success',
        });
        await refresh();
        if (onCancel) {
          onCancel();
        }
        if (data?.id) {
          history.push(`/academic-offer/admission-processes/${data.id}`);
        }
      },
      onError: {
        unknown: () => {
          addToast({
            icon: 'error',
            text: t('management.toasts.error'),
            color: 'danger',
          });
        },
      },
    },
  );

  const loading = updating || creating;

  const onSubmit = async (data: any) => {
    const formattedData = {
      ...data,
      firstInstallmentRule: data[`${PREFIX}_firstInstallmentRule`],
      selectDate: data[`${PREFIX}_selectDate`],
      selectMonth: data[`${PREFIX}_selectMonth`],
      selectYear: data[`${PREFIX}_selectYear`],
      selectDay: data[`${PREFIX}_selectDay`],
      validityRange: data[`${PREFIX}_validityRange`],
    };

    const firstInstallmentRuleValue = formatFirstInstallmentRule(
      formattedData,
      process?.firstInstallmentRule?.id,
    );

    const toDateOnly = (value: unknown): string => {
      if (value instanceof Date) {
        return dayjs(value).format('YYYY-MM-DD');
      }
      if (typeof value === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          return value;
        }
        return dayjs.utc(value).format('YYYY-MM-DD');
      }
      return '';
    };

    const commonData = {
      name: data.name,
      tuitionProcessTypeId: data.tuitionProcessTypeId.value,
      startedAt: toDateOnly(data.startedAt),
      endedAt: toDateOnly(data.endedAt),
      isMultiTuition: data.isMultiTuition.value,
      isEnabled: data.isEnabled.value,
      firstInstallmentRuleValue: firstInstallmentRuleValue ?? undefined,
      maxInstallments: data.maxInstallments?.value ?? null,
      payday: data.payday?.value ?? null,
    };

    if (isEdit && process) {
      await updateProcess({
        id: process.id,
        data: commonData,
      });
    } else {
      await createProcess({
        data: {
          ...commonData,
          templateId: data.templateId?.value ?? null,
          periodId: data.periodId.value,
        },
      });
    }
  };

  const booleanOptions = useMemo(
    () => [
      { value: true, label: t('common.yes') },
      { value: false, label: t('common.no') },
    ],
    [t],
  );

  return (
    <div className="mb-4">
      <div className="fs-18 font-weight-bold text-primary text-uppercase mb-3">
        {t('management.detailsTitle')}
      </div>
      <FormProvider {...methods}>
        <Row>
          <Col md={4}>
            <Select
              name="tuitionProcessTypeId"
              label={t('management.form.processType')}
              options={processTypeOptions}
              control={methods.control}
              rules={{ required: msgValidations.required }}
              disabled={loading || isEdit}
            />
          </Col>
          {!isEdit && (
            <Col md={4}>
              <Select
                name="templateId"
                label={t('management.form.template')}
                options={templateOptions as any}
                control={methods.control}
                rules={{ required: msgValidations.required }}
                disabled={loading}
                isClearable={false}
              />
            </Col>
          )}
          <Col md={4}>
            <TextInput
              name="name"
              label={t('management.form.processName')}
              control={methods.control}
              rules={{ required: msgValidations.required }}
              disabled={loading}
            />
          </Col>
          <Col md={4}>
            {isEdit ? (
              <TextInput
                name="periodName"
                label={t('management.form.academicPeriod')}
                control={methods.control}
                disabled={true}
              />
            ) : (
              <Select
                name="periodId"
                label={t('management.form.academicPeriod')}
                options={periods.map((p) => ({ value: p.id, label: p.name }))}
                control={methods.control}
                rules={{ required: msgValidations.required }}
                disabled={loading}
              />
            )}
          </Col>
          <Col md={4}>
            <DateInput
              name="startedAt"
              label={t('management.form.startDate')}
              control={methods.control}
              rules={{ required: msgValidations.required }}
              disabled={loading}
            />
          </Col>
          <Col md={4}>
            <DateInput
              name="endedAt"
              label={t('management.form.endDate')}
              control={methods.control}
              rules={{ required: msgValidations.required }}
              disabled={loading}
            />
          </Col>
          <Col md={4}>
            <Select
              name="isMultiTuition"
              label={t('management.form.multiTuition')}
              options={booleanOptions as any}
              control={methods.control}
              rules={{ required: msgValidations.required }}
              disabled={loading}
            />
          </Col>
          <Col md={4}>
            <Select
              name="isEnabled"
              label={t('management.form.enabled')}
              options={booleanOptions as any}
              control={methods.control}
              rules={{ required: msgValidations.required }}
              disabled={loading}
            />
          </Col>
          {isEdit && (
            <>
              <Col md={4}>
                <TextInput
                  name="quotas"
                  label={t('management.form.quotas')}
                  control={methods.control}
                  disabled={true}
                  tooltip={t('management.form.quotasTooltip')}
                />
              </Col>
              <Col md={4}>
                <TextInput
                  name="academicOfferQuota"
                  label={t('management.form.academicOfferQuota')}
                  control={methods.control}
                  disabled={true}
                  defaultValue={process?.academicOfferQuota}
                  tooltip={t('management.form.academicOfferQuotaTooltip')}
                />
              </Col>
            </>
          )}
        </Row>

        <Row>
          <Col md={4}>
            <Select
              name={`${PREFIX}_firstInstallmentRule`}
              label={t('management.form.firstInstallmentRule')}
              options={options}
              control={methods.control}
              disabled={loading}
            />
          </Col>
          <Col md={4}>
            <Row xs={12}>
              <Col xs={11}>
                <Select
                  name="maxInstallments"
                  label={t('management.form.maxInstallments')}
                  options={Array.from({ length: 12 }, (_, i) => ({
                    label: (i + 1).toString(),
                    value: i + 1,
                  }))}
                  control={methods.control}
                  disabled={loading}
                  // Vaciarlo vuelve a `null`, es decir "usar el máximo general".
                  isClearable
                />
              </Col>
              <Col
                xs={1}
                className="d-flex align-items-center"
                style={{ marginLeft: '-14px' }}
              >
                <Tooltip
                  id="info_tooltip_process_max_installments"
                  placement="bottom"
                  text={t('management.form.maxInstallmentsTooltip')}
                >
                  <Icon name="information_input" className="mb-3" />
                </Tooltip>
              </Col>
            </Row>
          </Col>
          <Col md={4}>
            <Row xs={12}>
              <Col xs={11}>
                <Select
                  name="payday"
                  label={t('management.form.payday')}
                  options={paydayOptions}
                  control={methods.control}
                  disabled={loading}
                  isClearable
                />
              </Col>
              <Col
                xs={1}
                className="d-flex align-items-center"
                style={{ marginLeft: '-14px' }}
              >
                <Tooltip
                  id="info_tooltip_process_payday"
                  placement="bottom"
                  text={t('management.form.paydayTooltip')}
                >
                  <Icon name="information_input" className="mb-3" />
                </Tooltip>
              </Col>
            </Row>
          </Col>
        </Row>
        {formFields}

        <div className="d-flex justify-content-end mt-3">
          <Button
            size="md"
            className="px-5 font-weight-bold mr-2"
            text={t('common.cancel')}
            onClick={onCancel}
            disabled={loading}
            outlined
          />
          <Button
            size="md"
            className="px-5 font-weight-bold"
            text={t('common.saveChanges')}
            onClick={methods.handleSubmit(onSubmit)}
            loading={loading}
            disabled={loading}
          />
        </div>
      </FormProvider>
    </div>
  );
}
